import { DocumentationContentType } from '@modules/clients/docSiteSearchType';
import {
  ESearchEventName,
  ESearchInteraction,
  ESearchSource,
} from '@modules/eventStream/enum/DocsSiteSearch';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { TSearchListItem } from '@modules/ui/search/types/SearchListItem';
import { TSearchResult } from '@modules/ui/search/types/SearchResult';
import { Locale } from '@rbx/intl';

export const isRealMouseClickEvent = (e: React.MouseEvent): boolean => {
  return e.isTrusted && e.detail > 0;
};

export type TDialogClosedInteraction =
  | ESearchInteraction.KeyboardEscape
  | ESearchInteraction.ModalClickOut
  | ESearchInteraction.SearchCompleted
  | ESearchInteraction.Shortcut;

export const trackSearchDialogClosed = ({
  interaction,
  locale,
  searchSessionId,
}: {
  // TODO: lhoward (2025/08/18): make interaction required
  interaction?: TDialogClosedInteraction;
  locale: Locale;
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.DialogClosed,
    parameters: {
      locale,
      refPageUrl: window.location.pathname,
      searchSessionId,
      ...(interaction ? { interaction } : {}),
    },
  });
};

export type TDialogOpenedInteraction =
  | ESearchInteraction.LandingSearchButton
  | ESearchInteraction.Shortcut
  | ESearchInteraction.NavSearchIcon;

export const trackSearchDialogOpened = ({
  interaction,
  locale,
  searchSessionId,
}: {
  // TODO: lhoward (2025/08/18): make interaction required
  interaction?: TDialogOpenedInteraction;
  locale: Locale;
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.DialogOpened,
    parameters: {
      locale,
      refPageUrl: window.location.pathname,
      searchSessionId,
      ...(interaction ? { interaction } : {}),
    },
  });
};

export type TPerformedInteraction = ESearchInteraction.Input;

export const trackSearchPerformed = ({
  category,
  interaction,
  isRecommended,
  isSemantic,
  locale,
  query,
  searchResults,
  searchSessionId,
}: {
  category: DocumentationContentType;
  interaction: TPerformedInteraction;
  isRecommended: boolean;
  isSemantic: boolean;
  locale: Locale;
  query: string;
  searchResults: TSearchResult[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.Performed,
    parameters: {
      interaction,
      isRecommended: isRecommended.toString(),
      isSemantic: isSemantic.toString(),
      locale,
      numCategories: new Set(searchResults.map((r) => r.documentationContentType)).size.toString(),
      numResults: searchResults.length.toString(),
      query,
      refPageUrl: window.location.pathname,
      resultCategories: searchResults.map((r) => r.documentationContentType).join('|'),
      resultTitles: searchResults.map((r) => r.title).join('|'),
      resultUrls: searchResults.map((r) => r.url).join('|'),
      searchSessionId,
      source: ESearchSource.Search,
      topicFilters: '',
      typeFilter: category.toString(),
    },
  });
};

export type TClickedInteraction =
  | ESearchInteraction.Click
  | ESearchInteraction.ClickWithCtrl
  | ESearchInteraction.ClickWithCmd
  | ESearchInteraction.KeyboardEnter
  | ESearchInteraction.KeyboardEnterWithCtrl
  | ESearchInteraction.KeyboardEnterWithCmd;

export type TResultClickedInteraction = TClickedInteraction;

/**
 * Detects if a modifier key (Ctrl on Windows/Linux, Cmd on Mac) was pressed during the event
 * @param e - Mouse or keyboard event
 * @param isKeyboardEvent - Whether this is a keyboard event (affects return type)
 * @returns The specific interaction type based on the modifier key pressed and event type
 */
export const getModifierKeyInteraction = (
  e: React.MouseEvent | React.KeyboardEvent,
  isKeyboardEvent = false,
): TResultClickedInteraction => {
  // Check for Mac Command key (metaKey)
  if (e.metaKey) {
    return isKeyboardEvent
      ? ESearchInteraction.KeyboardEnterWithCmd
      : ESearchInteraction.ClickWithCmd;
  }

  // Check for Ctrl key (Windows/Linux)
  if (e.ctrlKey) {
    return isKeyboardEvent
      ? ESearchInteraction.KeyboardEnterWithCtrl
      : ESearchInteraction.ClickWithCtrl;
  }

  if (isKeyboardEvent) {
    return ESearchInteraction.KeyboardEnter;
  }

  return ESearchInteraction.Click;
};

export const isModifierKeyInteraction = (interaction: TResultClickedInteraction): boolean => {
  return (
    interaction === ESearchInteraction.ClickWithCtrl ||
    interaction === ESearchInteraction.ClickWithCmd ||
    interaction === ESearchInteraction.KeyboardEnterWithCtrl ||
    interaction === ESearchInteraction.KeyboardEnterWithCmd
  );
};

export const trackSearchResultClicked = ({
  category,
  interaction,
  isRecommended,
  locale,
  query,
  searchResult,
  searchResults,
  searchSessionId,
}: {
  category: DocumentationContentType;
  interaction: TResultClickedInteraction;
  isRecommended: boolean;
  locale: Locale;
  query: string;
  searchResult: TSearchResult;
  searchResults: TSearchResult[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.ResultClicked,
    parameters: {
      interaction,
      isRecommended: isRecommended.toString(),
      isSemantic: (!!searchResult.isSemantic).toString(),
      locale,
      query,
      rank: searchResults.indexOf(searchResult).toString(),
      refPageUrl: window.location.pathname,
      resultTitle: searchResult.title,
      resultUrl: searchResult.url,
      searchSessionId,
      source: ESearchSource.SearchResults,
      topicFilters: '', // legacy
      typeFilter: category.toString(),
    },
  });
};

// lhoward (2025/08/15): New events for the Search UI Improvements are below

export type TAskAssistantClickedInteraction =
  | ESearchInteraction.Click
  | ESearchInteraction.KeyboardEnter;

export const trackSearchAskAssistantClicked = ({
  askAssistantLabel,
  interaction,
  locale,
  query,
  searchSessionId,
  searchResults,
}: {
  askAssistantLabel: string;
  interaction: TAskAssistantClickedInteraction;
  locale: Locale;
  query: string;
  searchSessionId: string;
  searchResults: TSearchResult[];
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.AskAssistantClicked,
    parameters: {
      askAssistantLabel,
      interaction,
      locale,
      query,
      refPageUrl: window.location.pathname,
      searchSessionId,
      numResults: searchResults.length.toString(),
    },
  });
};

export const trackSearchAskAssistantImpression = ({
  askAssistantLabel,
  locale,
  query,
  searchSessionId,
  searchResults,
}: {
  askAssistantLabel: string;
  locale: Locale;
  query: string;
  searchSessionId: string;
  searchResults: TSearchResult[];
}) => {
  unifiedLoggerClient.logImpressionEvent({
    eventName: ESearchEventName.AskAssistantImpression,
    parameters: {
      askAssistantLabel,
      locale,
      query,
      refPageUrl: window.location.pathname,
      searchSessionId,
      numResults: searchResults.length.toString(),
    },
  });
};

export type TCategoryClickedInteraction =
  | ESearchInteraction.ClickCategoryTitle
  | ESearchInteraction.KeyboardEnterCategoryTitle
  | ESearchInteraction.ClickCategoryPill
  | ESearchInteraction.KeyboardEnterCategoryPill;

export const trackSearchCategoryClicked = ({
  category,
  interaction,
  isRecommended,
  locale,
  query,
  searchResults,
  searchSessionId,
}: {
  category: DocumentationContentType;
  interaction: TCategoryClickedInteraction;
  isRecommended: boolean;
  locale: Locale;
  query: string;
  searchResults: TSearchResult[];
  searchSessionId: string;
}) => {
  const categories = Array.from(new Set(searchResults.map((r) => r.documentationContentType)));
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.CategoryClicked,
    parameters: {
      interaction,
      isRecommended: isRecommended.toString(),
      locale,
      numCategories: categories.length.toString(),
      query,
      rank: categories.indexOf(category).toString(),
      refPageUrl: window.location.pathname,
      resultCategories: searchResults.map((r) => r.documentationContentType).join('|'),
      searchSessionId,
      typeFilter: category.toString(),
    },
  });
};

export type TCategoryClearedInteraction =
  | ESearchInteraction.ClickCategoryPill
  | ESearchInteraction.ClickCategoryTitle
  | ESearchInteraction.KeyboardEnterCategoryPill
  | ESearchInteraction.KeyboardEnterCategoryTitle
  | ESearchInteraction.KeyboardBackspace
  | ESearchInteraction.ClearQueryButton;

export const trackSearchCategoryCleared = ({
  category,
  interaction,
  isRecommended,
  locale,
  query,
  searchResults,
  searchSessionId,
}: {
  category: DocumentationContentType;
  interaction: TCategoryClearedInteraction;
  isRecommended: boolean;
  locale: Locale;
  query: string;
  searchResults: TSearchResult[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.CategoryCleared,
    parameters: {
      interaction,
      isRecommended: isRecommended.toString(),
      locale,
      numResults: searchResults.length.toString(),
      query,
      refPageUrl: window.location.pathname,
      searchSessionId,
      typeFilter: category.toString(),
    },
  });
};

export type TQueryClearedInteraction = ESearchInteraction.Click;

export const trackSearchQueryCleared = ({
  category,
  interaction,
  isRecommended,
  locale,
  query,
  searchResults,
  searchSessionId,
}: {
  category: DocumentationContentType;
  interaction: TQueryClearedInteraction;
  isRecommended: boolean;
  locale: Locale;
  query: string;
  searchResults: TSearchResult[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.QueryCleared,
    parameters: {
      interaction,
      isRecommended: isRecommended.toString(),
      locale,
      numResults: searchResults.length.toString(),
      query,
      refPageUrl: window.location.pathname,
      searchSessionId,
      typeFilter: category.toString(),
    },
  });
};

export type TRecentlyVisitedClickedInteraction = TClickedInteraction;

export const trackSearchRecentlyVisitedClicked = ({
  interaction,
  item,
  locale,
  recentlyVisited,
  searchSessionId,
}: {
  interaction: TRecentlyVisitedClickedInteraction;
  item: TSearchListItem;
  locale: Locale;
  recentlyVisited: TSearchListItem[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.RecentlyVisitedClicked,
    parameters: {
      interaction,
      locale,
      numVisited: recentlyVisited.length.toString(),
      rank: recentlyVisited.indexOf(item).toString(),
      refPageUrl: window.location.pathname,
      searchSessionId,
      visitedTitle: item.title,
      visitedUrl: item.path ?? '',
    },
  });
};

export type TRecentlyVisitedDeletedInteraction =
  | ESearchInteraction.Click
  | ESearchInteraction.KeyboardEnter;

export const trackSearchRecentlyVisitedDeleted = ({
  interaction,
  item,
  locale,
  recentlyVisited,
  searchSessionId,
}: {
  interaction: TRecentlyVisitedDeletedInteraction;
  item: TSearchListItem;
  locale: Locale;
  recentlyVisited: TSearchListItem[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.RecentlyVisitedDeleted,
    parameters: {
      interaction,
      locale,
      numVisited: recentlyVisited.length.toString(),
      rank: recentlyVisited.indexOf(item).toString(),
      refPageUrl: window.location.pathname,
      searchSessionId,
      visitedTitle: item.title,
      visitedUrl: item.path ?? '',
    },
  });
};

export const trackSearchRecentlyVisitedImpression = ({
  locale,
  recentlyVisited,
  searchSessionId,
}: {
  locale: Locale;
  recentlyVisited: TSearchListItem[];
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logImpressionEvent({
    eventName: ESearchEventName.RecentlyVisitedImpression,
    parameters: {
      locale,
      numVisited: recentlyVisited.length.toString(),
      refPageUrl: window.location.pathname,
      searchSessionId,
      visitedTitles: recentlyVisited.map((r) => r.title).join('|'),
      visitedUrls: recentlyVisited.map((r) => r.path).join('|'),
    },
  });
};

export type TRecommendationClickedInteraction =
  | ESearchInteraction.Click
  | ESearchInteraction.KeyboardEnter;

export const trackSearchRecommendationClicked = ({
  interaction,
  locale,
  query,
  recommendation,
  searchSessionId,
}: {
  interaction: TRecommendationClickedInteraction;
  locale: Locale;
  query: string;
  recommendation: string;
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logClickEvent({
    eventName: ESearchEventName.RecommendationClicked,
    parameters: {
      interaction,
      locale,
      query,
      recommendation,
      refPageUrl: window.location.pathname,
      searchSessionId,
    },
  });
};

export const trackSearchRecommendationImpression = ({
  locale,
  query,
  recommendation,
  searchSessionId,
}: {
  locale: Locale;
  query: string;
  recommendation: string;
  searchSessionId: string;
}) => {
  unifiedLoggerClient.logImpressionEvent({
    eventName: ESearchEventName.RecommendationImpression,
    parameters: {
      locale,
      query,
      recommendation,
      refPageUrl: window.location.pathname,
      searchSessionId,
    },
  });
};
